import React, { useState, useEffect } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';

// Componentes
import SeccionPrincipal from './components/SeccionPrincipal';
import SeccionListaDinamica from './components/SeccionListaDinamica';
import SeccionPreguntas from './components/SeccionPreguntas';

// Hooks
import { useListaDinamica } from './hooks/useListaDinamica';

// Utilidades y Servicios
import { buildFormData, logFormData } from './utils/formDataBuilder';
import { guardarLandingPage, obtenerLandings } from './services/landingService';
import { getClientes } from "../../admin/Bitacoras/integracion";
import { convertirFormatoFecha, extraerHora, formateoBasico } from "../../../utilidades/formateos";


const SumaLanding = () => {
    const [data, setData] = useState([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filtroActivo, setFiltroActivo] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [landingSeleccionada, setLandingSeleccionada] = useState(null);
    const [opcionesClientes, setOpcionesClientes] = useState([]);

    // Estados del formulario - Sección Principal
    const [formPrincipal, setFormPrincipal] = useState({
        nombre: '',
        cliente: null,
        contenido: '',
        imagen: null,
        activo: true
    });

    // Hooks para gestionar Hipervínculos
    const hipervinculos = useListaDinamica();

    // Hooks para gestionar Contactos
    const contactos = useListaDinamica();

    // Estados de Preguntas Frecuentes
    const [preguntasFrecuentes, setPreguntasFrecuentes] = useState([]);
    const [preguntaDialog, setPreguntaDialog] = useState(false);
    const [preguntaEdicion, setPreguntaEdicion] = useState(null);
    const [formPregunta, setFormPregunta] = useState({
        pregunta: '',
        respuesta: ''
    });

    // Datos de ejemplo
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                await getClientes(setOpcionesClientes);
                // Datos de ejemplo para la tabla
                await obtenerLandings().then((res) => {
                    setData(res);
                });
            } catch (error) {
                console.error('Error al cargar datos:', error);
            }
        };

        cargarDatos();
    }, []);

    // Opciones de filtro activo/inactivo
    const opcionesFiltroActivo = [
        { label: 'Todos', value: null },
        { label: 'Activos', value: true },
        { label: 'Inactivos', value: false }
    ];

    // Template para la imagen
    const imagenBodyTemplate = (rowData) => {
        const imageUrl = rowData.imagen
            ? `${process.env.REACT_APP_API_URL || ''}/uploads/${rowData.imagen}`
            : 'https://via.placeholder.com/60';
        return (
            <img
                src={imageUrl}
                alt={rowData.nombre}
                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                onError={(e) => { e.target.src = 'https://via.placeholder.com/60'; }}
            />
        );
    };

    // Template para el estado activo
    const activoBodyTemplate = (rowData) => {
        return rowData.activo ? (
            <i className="pi pi-check" style={{ color: 'green', fontSize: '1.5rem' }}></i>
        ) : (
            <i className="pi pi-times" style={{ color: 'red', fontSize: '1.5rem' }}></i>
        );
    };
    const redirectToTalinkPreview = (landingId) => {
        const url = `https://talink.sumaenlinea.mx/${landingId}`;
        window.open(url, '_blank');
    }
    // Template para acciones
    const accionesBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                    icon="pi pi-pencil"
                    rounded
                    outlined
                    className="p-button-warning"
                    onClick={() => abrirEdicion(rowData)}
                    tooltip="Editar"
                />
                <Button
                    icon="pi pi-eye"
                    rounded
                    outlined
                    className="p-button-info"
                    onClick={() => redirectToTalinkPreview(rowData.id)}
                    tooltip="Ver"
                />
            </div>
        );
    };

    // Abrir formulario de nueva landing
    const abrirNueva = () => {
        setModoEdicion(false);
        setLandingSeleccionada(null);
        setFormPrincipal({ nombre: '', cliente: null, contenido: '', imagen: null, activo: true });
        hipervinculos.resetItems();
        contactos.resetItems();
        setPreguntasFrecuentes([]);
        setDialogVisible(true);
    };

    // Abrir formulario de edición
    const abrirEdicion = (landing) => {
        setModoEdicion(true);
        setLandingSeleccionada(landing);

        // Cargar datos principales
        setFormPrincipal({
            nombre: landing.nombre,
            cliente: opcionesClientes.find(c => c.code === landing.clienteId) || null,
            contenido: landing.contenido || '',
            imagen: landing.imagen,
            activo: landing.activo
        });

        // Cargar detalles si existen
        if (landing.detalles && landing.detalles.length > 0) {
            // Filtrar y cargar Hipervínculos (tipo 2)
            const hipervinculosData = landing.detalles
                .filter(d => d.tipo === 2)
                .sort((a, b) => a.orden - b.orden)
                .map(d => ({
                    id: d.id,
                    orden: d.orden,
                    titulo: d.titulo,
                    contenido: d.contenido,
                    hipervinculo: d.link || '',
                    imagen: d.imagen,
                    activo: d.activo
                }));
            hipervinculos.setItems(hipervinculosData);

            // Filtrar y cargar Contactos (tipo 3)
            const contactosData = landing.detalles
                .filter(d => d.tipo === 3)
                .sort((a, b) => a.orden - b.orden)
                .map(d => ({
                    id: d.id,
                    orden: d.orden,
                    titulo: d.titulo,
                    contenido: d.contenido,
                    hipervinculo: d.link || '',
                    imagen: d.imagen,
                    activo: d.activo
                }));
            contactos.setItems(contactosData);

            // Filtrar y cargar Preguntas Frecuentes (tipo 4)
            const preguntasData = landing.detalles
                .filter(d => d.tipo === 4)
                .sort((a, b) => a.orden - b.orden)
                .map(d => ({
                    id: d.id,
                    pregunta: d.titulo,
                    respuesta: d.contenido
                }));
            setPreguntasFrecuentes(preguntasData);
        } else {
            hipervinculos.resetItems();
            contactos.resetItems();
            setPreguntasFrecuentes([]);
        }

        setDialogVisible(true);
    };

    // Guardar Landing Page completa
    const guardarLanding = async () => {
        try {
            // Construir FormData
            const formData = buildFormData(
                formPrincipal,
                hipervinculos.items,
                contactos.items,
                preguntasFrecuentes,
                landingSeleccionada?.id // Pasar el ID si es edición
            );

            // Log para debugging
            logFormData(formData);

            // Enviar al backend
            const resultado = await guardarLandingPage(
                formData,
                modoEdicion,
                landingSeleccionada?.id
            );

            alert('Landing guardada correctamente');
            setDialogVisible(false);

            // Recargar datos
            const landings = await obtenerLandings();
            setData(landings);
        } catch (error) {
            console.error('Error al guardar:', error);
            alert('Error al guardar la landing: ' + error.message);
        }
    };

    // Header con filtro global
    const renderHeader = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span className="p-input-icon-left">
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => setGlobalFilterValue(e.target.value)}
                            placeholder="Búsqueda global"
                            style={{ width: '300px' }}
                        />
                    </span>
                    <Dropdown
                        value={filtroActivo}
                        options={opcionesFiltroActivo}
                        onChange={(e) => setFiltroActivo(e.value)}
                        placeholder="Filtrar por estado"
                        style={{ width: '200px' }}
                    />
                </div>
                <Button
                    label="Nueva Landing"
                    icon="pi pi-plus"
                    onClick={abrirNueva}
                    className="p-button-success"
                />
            </div>
        );
    };


    // Filtrado de datos
    const datosFiltrados = data.filter((item) => {
        // Filtro por activo/inactivo
        if (filtroActivo !== null && item.activo !== filtroActivo) {
            return false;
        }

        // Filtro global
        if (globalFilterValue) {
            const searchValue = globalFilterValue.toLowerCase();
            return (
                item.id.toString().includes(searchValue) ||
                item.nombre.toLowerCase().includes(searchValue) ||
                (item.clienteNombre && item.clienteNombre.toLowerCase().includes(searchValue)) ||
                (item.fecha && item.fecha.includes(searchValue)) ||
                (item.ultimaModificacion && item.ultimaModificacion.includes(searchValue))
            );
        }

        return true;
    });
    const ultimaModificacionBodyTemplate = (rowData) => {
        return <div>{convertirFormatoFecha(formateoBasico(rowData.ultimaModificacion)) +" "+ extraerHora(rowData.ultimaModificacion)}</div>;
    }
    const fechaBodyTemplate = (rowData) => {
        return <div>{convertirFormatoFecha(formateoBasico(rowData.fecha)) +" "+ extraerHora(rowData.fecha)}</div>;

    }
    return (
        <div style={{ padding: '2rem' }}>

            <DataTable
                value={datosFiltrados}
                scrollable
                scrollHeight="600px"
                virtualScrollerOptions={{ itemSize: 80 }}
                header={renderHeader()}
                emptyMessage="No se encontraron registros"
                stripedRows
                showGridlines
                style={{ marginTop: '1rem' }}
            >
                <Column
                    field="id"
                    header="ID"
                    style={{ minWidth: '80px' }}
                    sortable
                />
                <Column
                    field="nombre"
                    header="Nombre"
                    style={{ minWidth: '200px' }}
                    sortable
                />
                <Column
                    field="clienteNombre"
                    header="Cliente"
                    style={{ minWidth: '150px' }}
                    sortable
                />
                {/* <Column
                    header="Imagen"
                    body={imagenBodyTemplate}
                    style={{ minWidth: '100px' }}
                /> */}
                <Column
                    header="Activo"
                    body={activoBodyTemplate}
                    style={{ minWidth: '100px', textAlign: 'center' }}
                    sortable
                    sortField="activo"
                />
                <Column
                    field="fecha"
                    header="Fecha"
                    body={fechaBodyTemplate}
                    style={{ minWidth: '120px' }}
                    sortable
                />
                <Column
                    field="ultimaModificacion"
                    header="Última Modificación"
                    body={ultimaModificacionBodyTemplate}
                    style={{ minWidth: '180px' }}
                    sortable
                />
                <Column
                    header="Acciones"
                    body={accionesBodyTemplate}
                    style={{ minWidth: '120px', textAlign: 'center' }}
                />
            </DataTable>

            {/* Dialog del Formulario */}
            <Dialog
                visible={dialogVisible}
                style={{ width: '90vw', maxWidth: '1200px' }}
                header={modoEdicion ? 'Editar Landing Page' : 'Nueva Landing Page'}
                modal
                onHide={() => setDialogVisible(false)}
                footer={
                    <div>
                        <Button label="Cancelar" icon="pi pi-times" onClick={() => setDialogVisible(false)} className="p-button-text" />
                        <Button label="Guardar" icon="pi pi-check" onClick={guardarLanding} />
                    </div>
                }
            >
                <TabView>
                    {/* Sección 1: Principal */}
                    <TabPanel header="Principal">
                        <SeccionPrincipal
                            formData={formPrincipal}
                            setFormData={setFormPrincipal}
                            opcionesClientes={opcionesClientes}
                        />
                    </TabPanel>

                    {/* Sección 2: Hipervínculos */}
                    <TabPanel header="Hipervínculos">
                        <SeccionListaDinamica
                            titulo="Hipervínculo"
                            items={hipervinculos.items}
                            setItems={hipervinculos.setItems}
                            dialogVisible={hipervinculos.dialogVisible}
                            setDialogVisible={hipervinculos.setDialogVisible}
                            itemEdicion={hipervinculos.itemEdicion}
                            setItemEdicion={hipervinculos.setItemEdicion}
                            formItem={hipervinculos.formItem}
                            setFormItem={hipervinculos.setFormItem}
                            agregarItem={hipervinculos.agregarItem}
                            editarItem={hipervinculos.editarItem}
                            guardarItem={hipervinculos.guardarItem}
                            eliminarItem={hipervinculos.eliminarItem}
                            moverItem={hipervinculos.moverItem}
                        />
                    </TabPanel>

                    {/* Sección 3: Contacto */}
                    <TabPanel header="Contacto">
                        <SeccionListaDinamica
                            titulo="Contacto"
                            items={contactos.items}
                            setItems={contactos.setItems}
                            dialogVisible={contactos.dialogVisible}
                            setDialogVisible={contactos.setDialogVisible}
                            itemEdicion={contactos.itemEdicion}
                            setItemEdicion={contactos.setItemEdicion}
                            formItem={contactos.formItem}
                            setFormItem={contactos.setFormItem}
                            agregarItem={contactos.agregarItem}
                            editarItem={contactos.editarItem}
                            guardarItem={contactos.guardarItem}
                            eliminarItem={contactos.eliminarItem}
                            moverItem={contactos.moverItem}
                        />
                    </TabPanel>

                    {/* Sección 4: Preguntas Frecuentes */}
                    <TabPanel header="Preguntas Frecuentes">
                        <SeccionPreguntas
                            preguntas={preguntasFrecuentes}
                            setPreguntas={setPreguntasFrecuentes}
                            dialogVisible={preguntaDialog}
                            setDialogVisible={setPreguntaDialog}
                            preguntaEdicion={preguntaEdicion}
                            setPreguntaEdicion={setPreguntaEdicion}
                            formPregunta={formPregunta}
                            setFormPregunta={setFormPregunta}
                        />
                    </TabPanel>
                </TabView>
            </Dialog>
        </div>
    );
};

export default SumaLanding;